// CustomerView — the project AS THE CUSTOMER SEES IT.
//
// Alex, 2026-08-08: "a customer working on one project will have to see only
// their own project and maybe not the pricing… my main concern was to avoid
// making UI or code mistakes, and this safety and security can be at the lowest
// level." 2026-08-12: "Better to move to the customer screen."
//
// ⚠ THE ONE RULE THIS FILE HAS: it may fetch `/api/customer/...` AND NOTHING
// ELSE. Every internal endpoint returns cost — `/api/projects/:id` alone carries
// 164 money-shaped keys — so a single convenience fetch to an internal route,
// added later by someone wanting "just the totals", re-opens the whole hole that
// src/customer-view.js was built to close. That is not left to discipline:
// `scripts/check-customer-view.mjs` greps this file for any fetch URL outside
// `/api/customer/` and fails `npm run check`.
//
// For the same reason this file imports NO cost helper — no MoneyCell, no
// product-resolver, no format-cols. If a number cannot be rendered from what
// `/api/customer/projects/:id` sent, it does not belong on this screen.
//
// NOT A GRID, on purpose. Every other screen here is a spreadsheet because every
// other screen is a work surface. This one is a READ — the customer is not
// editing anything — so it reads as a document: what is in the project, how it
// is configured, what is included — the product, its configuration, and one
// authored sentence saying what is in it.
//
// NO AUTH YET (INF-4). Nothing here decides WHO may look; the serializer decides
// WHAT the answer contains. When login lands it gates the route and this file
// does not change.

import { useEffect, useMemo, useState } from 'react';
import HierarchyWorkspace from './components/HierarchyWorkspace.jsx';
import ViewSwitcher from './components/ViewSwitcher.jsx';
import SectionTitle from './components/SectionTitle.jsx';
import Notice from './components/Notice.jsx';
import { ParamLine } from './components/PlacementConfigSubRow.jsx';

// A function with nothing placed under it, anywhere in its subtree, is scope we
// have not quoted — a placeholder in OUR tree, not a line in the customer's.
// Showing it says "you are getting this" with nothing behind it. Measured on the
// sample project: 101 of 165 functions are empty leaves, so without this the
// tree is mostly things that do not exist yet.
// ⚠ Alex's call to flip — if a proposal should show the full intended structure
// including the not-yet-priced parts, delete this one function and pass the tree
// through. The rest of the screen does not care.
function pruneEmpty(nodes) {
  const out = [];
  for (const n of nodes) {
    const children = pruneEmpty(n.children || []);
    if (children.length || (n.placements || []).length) out.push({ ...n, children });
  }
  return out;
}

function toTreeNodes(fns) {
  return fns.map(f => ({
    id: `func:${f.id}`,
    code: f.code || '',
    label: f.label || '',
    children: toTreeNodes(f.children || []),
  }));
}

function findFn(fns, id) {
  for (const f of fns) {
    if (`func:${f.id}` === id) return f;
    const hit = findFn(f.children || [], id);
    if (hit) return hit;
  }
  return null;
}

// Every function at or under `f`, in document order — so selecting a branch
// reads as the whole branch rather than an empty page with children hidden
// behind more clicks. A proposal is read top to bottom.
function flatten(f, depth = 0, out = []) {
  out.push({ fn: f, depth });
  for (const c of f.children || []) flatten(c, depth + 1, out);
  return out;
}

// ⚠ NO LOCAL ParamLine ANY MORE (Fable sweep R4, 2026-08-13). This screen had its
// own copy of "render one configuration parameter", and CustomerConfigureView had a
// third — one job, three implementations, two of them written the same week.
//
// The shared one lives in components/PlacementConfigSubRow.jsx and is
// PRESENTATION-PURE: it imports only icons.js, SelectCell and FormulaCell, so
// nothing that computes or formats cost comes with it. That is not a hopeful
// reading — check-customer-view.mjs now walks this file's whole import graph and
// fails on any cost module or internal API path at ANY depth, so the boundary is
// enforced rather than trusted.
//
// `atTypicalOverride` is why it fits without a new prop: this screen's notion of
// "at the typical" is the serializer's `overridden` flag, NOT value === typical. A
// customer who deliberately chooses 0.8 when the typical IS 0.8 has still chosen
// it, and the payload says so. The component already had that seam.
// What is included, as a sentence (Alex, 2026-08-12). This replaced a three-column
// table of resolved catalog lines with quantities — "Pipe · 15 m", "90° Elbow · 3
// each". A customer buys the finished product, so the itemisation was both the wrong
// altitude and our internal vocabulary. The text is authored on the product; when
// nobody has written one the block simply says nothing rather than falling back to
// the line list, because the fallback IS the thing being removed.
function IncludesLine({ text }) {
  if (!text) return null;
  return (
    <div style={{
      fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)',
      maxWidth: 620, lineHeight: 1.45,
    }}>{text}</div>
  );
}

function PlacementBlock({ p }) {
  return (
    <div style={{
      border: '1px solid var(--th-border-subtle)', borderRadius: 3,
      padding: '8px 10px', marginBottom: 8, background: 'var(--th-row-even)',
    }}>
      <div style={{ fontWeight: 600, fontSize: 'var(--fs-small)', color: 'var(--th-navy)', marginBottom: 6 }}>
        {p.label || p.product?.label || 'Item'}
      </div>
      {p.configuration.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {p.configuration.map((c, i) => (
            <ParamLine key={i} p={c} value={c.value} readOnly atTypicalOverride={!c.overridden} />
          ))}
        </div>
      )}
      <IncludesLine text={p.description} />
    </div>
  );
}

function FunctionBlock({ fn, depth }) {
  return (
    <section style={{ marginBottom: 18, marginLeft: depth * 14 }}>
      <div style={{
        display: 'flex', gap: 8, alignItems: 'baseline',
        borderBottom: '2px solid var(--th-border)', paddingBottom: 3, marginBottom: 8,
      }}>
        <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-navy)' }}>
          {fn.code}
        </span>
        <span style={{ fontWeight: 600, fontSize: 'var(--fs-small)' }}>{fn.label || ''}</span>
        {fn.qty != null && fn.qty !== 1 && (
          <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)' }}>× {fn.qty}</span>
        )}
      </div>
      {fn.placements.length === 0 ? (
        <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)', fontStyle: 'italic' }}>
          Detailed under the sections below.
        </div>
      ) : fn.placements.map(p => <PlacementBlock key={p.id} p={p} />)}
    </section>
  );
}

export default function CustomerView({ view, setView, activeProjectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  useEffect(() => {
    if (!activeProjectId) { setData(null); setError(null); return; }
    let alive = true;
    setLoading(true); setError(null);
    // THE ONLY FETCH IN THIS FILE. See the header.
    fetch(`/api/customer/projects/${encodeURIComponent(activeProjectId)}`)
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.error || 'Could not load this project.')))))
      .then(d => { if (alive) { setData(d); setSelectedNodeId(null); } })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeProjectId]);

  const functions = useMemo(() => pruneEmpty(data?.functions || []), [data]);
  const treeNodes = useMemo(() => toTreeNodes(functions), [functions]);

  const selected = selectedNodeId ? findFn(functions, selectedNodeId) : null;
  const blocks = selected ? flatten(selected) : functions.flatMap(f => flatten(f));

  const header = (
    <div style={{ marginBottom: 14 }}>
      <SectionTitle>{data ? `${data.project.code} — ${data.project.customer}` : 'Project'}</SectionTitle>
      <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
        {selected
          ? `${selected.code} ${selected.label || ''}`
          : `${blocks.length} section${blocks.length === 1 ? '' : 's'} — everything included in this project.`}
      </div>
    </div>
  );

  // Notice is for something WRONG (it renders an alert). "No project picked" and
  // "nothing placed yet" are neither wrong nor actionable-by-alarm, so they are
  // plain muted text — the same reason the catalog does not alert on an empty shelf.
  const muted = (t) => <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>{t}</div>;

  let body;
  if (!activeProjectId) {
    body = muted('Pick a project in the banner above to see it as the customer does.');
  } else if (loading) {
    body = muted('Loading…');
  } else if (error) {
    body = <Notice kind="warning">{error}</Notice>;
  } else if (!blocks.length) {
    body = muted('Nothing is placed in this project yet, so there is nothing for a customer to read.');
  } else {
    body = blocks.map(({ fn, depth }) => <FunctionBlock key={fn.id} fn={fn} depth={selected ? depth : Math.min(depth, 1)} />);
  }

  return (
    <HierarchyWorkspace
      treeNodes={treeNodes}
      selectedNodeId={selectedNodeId}
      onSelectNode={setSelectedNodeId}
      treeHeader={<ViewSwitcher view={view} setView={setView} onSelectRoot={() => setSelectedNodeId(null)} />}
      filterPlaceholder="Filter sections…"
      storageKey="customer"
      mainHeader={header}
    >
      <div style={{ maxWidth: 780 }}>{body}</div>
    </HierarchyWorkspace>
  );
}
